/**
 * src/api/team.js
 *
 * All API calls related to teams and team members.
 * Bearer token is attached automatically from the auth cookie.
 */

import { authedJson } from './http'

const apiGet = path => authedJson('GET', path)
const apiPost = path => authedJson('POST', path)
const apiDelete = path => authedJson('DELETE', path)
const apiPostJson = (path, body) => authedJson('POST', path, body)

/**
 * Fetch the metric cards for the top of the Team page.
 * GET /metrics/skills/
 *
 * Returns a bare array of metric objects:
 *   { name, value, description, cardtype: 'number'|'bar',
 *     icon, evidence_header?, evidence? }
 * For cardtype 'bar', value is a JSON-encoded object of label→percent (0–100).
 * @returns {Promise<Array<object>>}
 */
export async function fetchTeamMetrics() {
  return apiGet('/metrics/skills/')
}

/**
 * Fetch the combined Team-page payload in a single request: the full ordered list
 * of teams (for the filter dropdown) plus every member with the teams they belong to.
 *
 * Team names live once, under `teams`. Each member's `teams` entry carries only
 * `{ id, is_primary }` — resolve the name from the top-level `teams` list.
 *
 * `skills` is the whole accepted catalog (Skills filter dropdown). Each member's
 * `skill_ids` is the set of skill ids the member holds OR has a pending proposal
 * for — the Skills filter matches a member when its selected id is in `skill_ids`.
 * GET /teams/members
 * @returns {Promise<{
 *   teams: Array<{ id: number, name: string }>,
 *   skills: Array<{ id: number, name: string }>,
 *   members: Array<{
 *     id: number, name: string, initials: string, role: string,
 *     teams: Array<{ id: number, is_primary: boolean }>,
 *     skill_ids: number[],
 *     skills_summary: Record<'Core'|'Technical'|'General'|'Total', string>, new_skills: number,
 *     cases: { resolved: number, assisted: number, avg_resolution: string, first_response: string }
 *   }>
 * }>}
 */
export async function fetchTeamMembers() {
  return apiGet('/teams/members')
}

/**
 * Fetch the per-agent skill detail shown in the agent Details modal: active
 * skills, AI-proposed skills pending approval, a per-classification summary, and
 * the agent's headline metrics.
 * GET /skills/agents/{agentId}
 * @param {number|string} agentId
 * @returns {Promise<{
 *   skills: Array<{ name: string, skill_id: number, type: string,
 *                   last_used: string, count: number, status: string,
 *                   locked: boolean }>,
 *   proposed_skills: Array<{ name: string, skill_id: number, type: string }>,
 *   skills_summary: Record<string, string>,
 *   metrics: Array<{ metric_name: string, metric_value: string, metric_tag: string }>
 * }>}
 */
export async function fetchAgentSkills(agentId) {
  return apiGet(`/skills/agents/${agentId}`)
}

/**
 * Fetch the evidence cases (ticket numbers) that justify a given skill for an
 * agent — shown in the Review modal's per-skill evidence section. Each returned
 * ticket can be opened individually for AI evidence via the cases API.
 * GET /skills/evidence/{agentId}/{skillId}
 * @param {number|string} agentId
 * @param {number|string} skillId
 * @returns {Promise<{ evidence: string[] }>}
 */
export async function fetchSkillEvidenceCases(agentId, skillId) {
  return apiGet(`/skills/evidence/${agentId}/${skillId}`)
}

/**
 * Fetch the full AI-generated evidence for a single ticket — shown in the
 * ticket evidence modal opened from an evidence-case badge. Keyed solely by the
 * ticket id (no skill).
 * GET /teams/ticket/evidence/{ticketId}
 * @param {number|string} ticketId
 * @returns {Promise<{ evidence: {
 *   ticket_number: string, short_description: string, ticket_summary: string,
 *   root_cause_category: string, root_cause_detail: string,
 *   resolving_agent: string|null,
 *   skills:   Array<{ id: number|null, name: string, reason: string }>,
 *   symptoms: Array<{ name: string, symptom_detail: string }>,
 *   problems: Array<{ name: string, description: string }>,
 * } }>}
 */
export async function fetchTicketEvidence(ticketId) {
  return apiGet(`/teams/ticket/evidence/${encodeURIComponent(ticketId)}`)
}

/**
 * Detach a skill from a ticket's evidence. Called when a user confirms
 * downvoting a skill in the case evidence modal.
 * DELETE /teams/ticket/{ticketNumber}/skill/{skillId}
 * @param {number|string} ticketNumber
 * @param {number|string} skillId
 * @returns {Promise<{ status: string, ticket_number: string, skill_id: number }>}
 */
export async function removeTicketSkill(ticketNumber, skillId) {
  return apiDelete(`/teams/ticket/${encodeURIComponent(ticketNumber)}/skill/${encodeURIComponent(skillId)}`)
}

/**
 * Apply a per-skill action for an agent from the Team member Details modal.
 * POST /agents/skill/action/{action}/{agentId}/{skillId}
 *
 * @param {number|string} agentId
 * @param {number|string} skillId
 * @param {'up'|'down'|'lock'|'unlock'|'delete'|'approve'|'reject'} action
 * @returns {Promise<{
 *   status: string, agent_id: number, skill_id: number,
 *   level?: string, assessment_score?: number, locked?: boolean,
 *   deleted?: number, rejected?: number, inserted?: number, promoted?: number
 * }>}
 */
export async function applySkillAction(agentId, skillId, action) {
  return apiPost(`/agents/skill/action/${action}/${agentId}/${skillId}`)
}

/**
 * Disable a team member (Team member modal -> Disable user): sets the agent's
 * active flag to false. Disabled agents drop out of the Team page and are not
 * re-activated by the roster sync.
 * POST /agents/{agentId}/disable
 * @param {number|string} agentId
 * @returns {Promise<{ status: string, agent_id: number, is_active: false }>}
 */
export async function disableAgent(agentId) {
  return apiPost(`/agents/${agentId}/disable`)
}

/**
 * Assign a single skill to one or more team members, from the Skill Details
 * screen's Assign modal. An agent with a matching AI-proposed suggestion for the
 * skill has that proposal promoted; an agent with none gets a fresh assignment.
 * Agents who already hold the skill are skipped server-side.
 * POST /agents/skill/assign
 *
 * @param {number|string} skillId
 * @param {Array<number|string>} agentIds
 * @returns {Promise<{
 *   status: string, skill_id: number,
 *   assigned: number[], promoted: number, skipped: number[]
 * }>}
 */
export async function assignSkillToAgents(skillId, agentIds) {
  return apiPostJson('/agents/skill/assign', { skill_id: skillId, agent_ids: agentIds })
}

/**
 * Assign one or more skills to a single team member, from the agent Details screen's
 * Assign Skill modal (the transpose of assignSkillToAgents). A skill with a matching
 * AI-proposed suggestion for the agent has that proposal promoted; a skill with none
 * gets a fresh assignment. Skills the agent already holds are skipped server-side.
 * POST /agents/skills/assign
 *
 * @param {number|string} agentId
 * @param {Array<number|string>} skillIds
 * @returns {Promise<{
 *   status: string, agent_id: number,
 *   assigned: number[], promoted: number, skipped: number[]
 * }>}
 */
export async function assignSkillsToAgent(agentId, skillIds) {
  return apiPostJson('/agents/skills/assign', { agent_id: agentId, skill_ids: skillIds })
}
